/**
 * PinLockOverlay.jsx
 *
 * Full-viewport frosted-glass overlay shown when the session is locked or
 * when a required-role user has no PIN set yet. Captures all pointer/keyboard
 * events; the underlying app remains rendered (and screen-readable as
 * inert via aria-hidden on #main-content) but inaccessible.
 *
 * UX:
 *   - Shows signed-in user's initials + name so the wrong person knows to
 *     sign out rather than guess.
 *   - Numeric keypad (touch-friendly) + keyboard input.
 *   - "Not me — sign out" link returns to MSAL login.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../auth/AuthContext.jsx';
import { usePinLock } from '../contexts/PinLockContext.jsx';
import ForgotPinDialog from './ForgotPinDialog.jsx';

const PIN_MIN = 6;
const PIN_MAX = 6;

function Keypad({ onDigit, onBackspace, disabled }) {
  const keys = ['1','2','3','4','5','6','7','8','9','','0','⌫'];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginTop: 18 }}>
      {keys.map((k, i) => {
        if (!k) return <div key={i} />;
        const isBack = k === '⌫';
        return (
          <button
            key={i}
            type="button"
            disabled={disabled}
            onClick={() => isBack ? onBackspace() : onDigit(k)}
            className="btn"
            style={{
              padding: '14px 0',
              fontSize: 20,
              fontWeight: 500,
              background: 'var(--surface)',
              border: '1px solid var(--border-strong)',
              borderRadius: 12,
              color: isBack ? 'var(--text-3)' : 'var(--text-1, #111)',
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.5 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0,
            }}
          >
            {k}
          </button>
        );
      })}
    </div>
  );
}

function PinDots({ length, max }) {
  return (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 12, minHeight: 16 }}>
      {Array.from({ length: max }).map((_, i) => (
        <span
          key={i}
          style={{
            width: 12, height: 12, borderRadius: '50%',
            background: i < length ? 'var(--brand)' : 'transparent',
            border: '1.5px solid ' + (i < length ? 'var(--brand)' : 'var(--border-strong)'),
            transition: 'background 120ms, border-color 120ms',
          }}
        />
      ))}
    </div>
  );
}

export default function PinLockOverlay() {
  const { user, logout } = useAuth();
  const lock = usePinLock();
  const isSetup = lock?.stage === 'setup';
  const isLocked = lock?.stage === 'locked';

  const [pin, setPin]           = useState('');
  const [confirm, setConfirm]   = useState('');
  const [phase, setPhase]       = useState('enter');   // setup: 'enter' → 'confirm'
  const [currentPin, setCurrent] = useState('');        // unused on first-time setup; reserved
  const [busy, setBusy]         = useState(false);
  const [error, setError]       = useState(null);
  const [forgotOpen, setForgotOpen] = useState(false);
  const containerRef            = useRef(null);

  // Mark the app behind us inert so screen readers + tab order skip it.
  useEffect(() => {
    const main = document.getElementById('main-content');
    const aside = document.querySelector('aside[aria-label="Main navigation"]');
    [main, aside].forEach(el => el && el.setAttribute('aria-hidden', 'true'));
    return () => {
      [main, aside].forEach(el => el && el.removeAttribute('aria-hidden'));
    };
  }, []);

  // Capture keyboard input globally while the overlay is up.
  useEffect(() => {
    function onKey(e) {
      if (busy) return;
      if (/^\d$/.test(e.key)) handleDigit(e.key);
      else if (e.key === 'Backspace') handleBackspace();
      else if (e.key === 'Enter') tryAdvance();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin, confirm, phase, busy]);

  const activeValue = isSetup && phase === 'confirm' ? confirm : pin;
  const setActiveValue = (v) => isSetup && phase === 'confirm' ? setConfirm(v) : setPin(v);

  function handleDigit(d) {
    setError(null);
    if (activeValue.length >= PIN_MAX) return;
    setActiveValue(activeValue + d);
  }
  function handleBackspace() {
    setError(null);
    setActiveValue(activeValue.slice(0, -1));
  }

  async function tryAdvance() {
    if (busy) return;
    if (isSetup) {
      if (phase === 'enter') {
        if (pin.length < PIN_MIN) { setError(`PIN must be at least ${PIN_MIN} digits.`); return; }
        if (/^(\d)\1+$/.test(pin)) { setError('PIN cannot be a single repeating digit.'); return; }
        setPhase('confirm');
        return;
      }
      // confirm phase
      if (confirm !== pin) {
        setError('PINs do not match. Start over.');
        setPin(''); setConfirm(''); setPhase('enter');
        return;
      }
      setBusy(true);
      try {
        await lock.submitSetup(pin, currentPin || undefined);
      } catch (e) {
        setError(e.message || 'Could not save PIN.');
      } finally { setBusy(false); }
      return;
    }

    // Locked → verify
    if (pin.length < PIN_MIN) { setError(`PIN must be at least ${PIN_MIN} digits.`); return; }
    setBusy(true);
    try {
      await lock.submitUnlock(pin);
    } catch (e) {
      setError(e.message || 'Could not verify PIN.');
      setPin('');
    } finally { setBusy(false); }
  }

  const headline = useMemo(() => {
    if (isSetup) return phase === 'enter' ? 'Set a session PIN' : 'Confirm your PIN';
    return 'Session locked';
  }, [isSetup, phase]);

  const sub = useMemo(() => {
    if (isSetup) return phase === 'enter'
      ? `Choose a ${PIN_MIN}-digit PIN. You'll enter it when the screen locks.`
      : 'Type the same PIN again to confirm.';
    return 'Enter your PIN to continue. Sign out if this isn\'t you.';
  }, [isSetup, phase]);

  const initials = (user?.initials) || (user?.name || '?').split(' ').slice(0,2).map(s => s[0]).join('').toUpperCase();

  return (
    <div
      ref={containerRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="pinlock-title"
      style={{
        position: 'fixed', inset: 0, zIndex: 9000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(20, 30, 45, 0.45)',
        backdropFilter: 'blur(18px) saturate(140%)',
        WebkitBackdropFilter: 'blur(18px) saturate(140%)',
        padding: 16,
      }}
    >
      <div
        style={{
          width: '100%', maxWidth: 380,
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 16,
          padding: 28,
          boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
        }}
      >
        {/* Identity row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
          <div
            className="avatar"
            style={{
              width: 44, height: 44, borderRadius: '50%',
              background: 'linear-gradient(135deg, var(--brand) 0%, #14B8A6 100%)',
              color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 600, fontSize: 15,
            }}
          >
            {initials}
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-1, #111)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.name || 'Signed in'}</div>
            <div style={{ fontSize: 12, color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.email || ''}</div>
          </div>
        </div>

        <h2 id="pinlock-title" style={{ fontSize: 18, fontWeight: 600, margin: 0, color: 'var(--text-1, #111)' }}>{headline}</h2>
        <p style={{ fontSize: 13, color: 'var(--text-3)', margin: '6px 0 0' }}>{sub}</p>

        <PinDots length={activeValue.length} max={PIN_MIN} />

        {error && (
          <div
            role="alert"
            className="callout danger"
            style={{ marginTop: 14, fontSize: 12, padding: '8px 10px' }}
          >
            <div style={{ flex: 1 }}>{error}</div>
          </div>
        )}

        <Keypad
          onDigit={handleDigit}
          onBackspace={handleBackspace}
          disabled={busy}
        />

        <button
          type="button"
          className="btn primary"
          style={{ width: '100%', marginTop: 16, padding: '12px 0', fontSize: 14, fontWeight: 600, justifyContent: 'center', gap: 0 }}
          onClick={tryAdvance}
          disabled={busy || activeValue.length < PIN_MIN}
        >
          {busy ? 'Please wait…' : (isSetup ? (phase === 'enter' ? 'Continue' : 'Save PIN') : 'Unlock')}
        </button>

        {isLocked && (
          <>
            <button
              type="button"
              onClick={() => setForgotOpen(true)}
              className="btn ghost"
              style={{ width: '100%', marginTop: 8, padding: '8px 0', fontSize: 12, color: 'var(--brand-ink)', justifyContent: 'center', gap: 0 }}
            >
              Forgot your PIN?
            </button>
            <button
              type="button"
              onClick={logout}
              className="btn ghost"
              style={{ width: '100%', marginTop: 4, padding: '8px 0', fontSize: 12, color: 'var(--text-3)', justifyContent: 'center', gap: 0 }}
            >
              Not me — sign out
            </button>
          </>
        )}
        {isSetup && phase === 'confirm' && (
          <button
            type="button"
            onClick={() => { setConfirm(''); setPin(''); setPhase('enter'); setError(null); }}
            className="btn ghost"
            style={{ width: '100%', marginTop: 8, padding: '10px 0', fontSize: 12, color: 'var(--text-3)', justifyContent: 'center', gap: 0 }}
          >
            Start over
          </button>
        )}
      </div>

      {forgotOpen && (
        <ForgotPinDialog onClose={() => setForgotOpen(false)} />
      )}
    </div>
  );
}
